<template>
  <div class="signature" :class="{ darkmode }">
    <h6>{{ props.label }}</h6>
    <p>Unterschreiben Sie mit Ihrem Finger in der gerahmten Box.</p>
    <span class="hint">
      <svg
        width="103"
        height="72"
        viewBox="0 0 103 72"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M7.52571 2H23.5257H31.5258C33.9258 2 40.5258 2 40.5258 5.99994V12C40.6924 28.1667 40.9258 61.5 40.5258 65.5C40.1258 69.5 36.3591 69.8333 34.5258 69.5C26.5257 69.6667 9.62571 69.9 6.02571 69.5C2.42571 69.1 1.85904 65.3333 2.02571 63.5V7C2.02571 3.8 5.69237 2.33333 7.52571 2Z"
          stroke="#5AA5A5"
          stroke-width="4"
        />
        <path
          d="M96.8899 28.4846L98.8387 44.3654L99.8132 52.3059C100.106 54.688 100.909 61.2389 96.9393 61.7261L90.9839 62.4569C74.9579 64.5916 41.9012 68.8833 37.8822 68.9735C33.8633 69.0637 33.0736 65.3657 33.1812 63.5054C32.0413 55.5853 29.7512 38.8395 29.7097 35.2176C29.6683 31.5956 33.3379 30.5744 35.1779 30.5165L91.2572 23.6345C94.4333 23.2448 96.3357 26.7055 96.8899 28.4846Z"
          stroke-width="4"
        />
        <path
          d="M47 7.2208C55.5 3.2208 72.6 -0.279197 73 17.7208M67 13.2208L73 19.7208L77.5 11.7208"
          stroke-width="4"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>

      <p class="text-s">
        <b>Unser Tipp:</b>
        <br />
        Drehen Sie Ihr Smartphone auf die Seite, um mehr Platz für Ihre Unterschrift zu haben.
      </p>
    </span>
    <canvas ref="signatureCanvas" @touchmove.prevent></canvas>

    <PflegeButton
      icon="reset-line"
      label="Neuer Versuch"
      variant="text"
      @click="clear"
    />

    <PflegeButton
      v-if="!disabled"
      label="Unterschrift bestätigen"
      @click="save"
    />
  </div>
</template>

<script setup>
import SignaturePad from 'signature_pad'
import { onMounted, onUnmounted, ref, watch } from 'vue'
import PflegeButton from '../Button'

const ratio = Math.max(window.devicePixelRatio || 1, 1)
const signatureCanvas = ref(null)
const disabled = ref(false)
const modelValue = defineModel()

const emit = defineEmits(['empty', 'input'])
const props = defineProps({
  sigOption: {
    type: Object,
    required: false,
    default: () => {
      return {
        backgroundColor: 'rgb(255,255,255)',
        penColor: 'rgb(0, 0, 0)',
      }
    },
  },
  label: {
    type: String,
    required: false,
    default: 'Ihre digitale Unterschrift',
  },
  darkmode: {
    type: Boolean,
    required: false,
    default: false,
  },
})

let signaturePad

const resizeCanvas = (canvas) => {
  const context = canvas.getContext('2d')
  canvas.width = canvas.offsetWidth * ratio
  canvas.height = canvas.offsetHeight * ratio
  context.scale(ratio, ratio)
}

const draw = () => {
  const canvas = signatureCanvas.value
  signaturePad = new SignaturePad(canvas, props.sigOption)
  resizeCanvas(canvas)

  if (modelValue.value) {
    signaturePad.fromDataURL(modelValue.value)
    disabled.value = true
  }
}

const clear = () => {
  signaturePad.clear()
  disabled.value = false
}

const save = () => {
  if (!signaturePad.isEmpty()) {
    const svg = signaturePad.toDataURL('image/svg+xml')
    modelValue.value = svg
    emit('input', svg)
    disabled.value = true
  } else {
    emit('empty')
  }
}

const onResize = () => {
  // Save the current signature data
  const savedData = signaturePad.isEmpty() ? null : signaturePad.toDataURL()
  
  // Resize the canvas
  resizeCanvas(signatureCanvas.value)
  
  // Reinitialize the signature pad (if needed) and restore the signature
  if (savedData) {
    signaturePad.fromDataURL(savedData)
  }
}

watch(disabled, (val) => {
  if (val) {
    signaturePad.off()
  } else {
    signaturePad.on()
  }
})

onMounted(() => {
  window.addEventListener('resize', onResize)
  draw()
})

onUnmounted(() => {
  window.removeEventListener('resize', onResize)
})
</script>

<style lang="scss" scoped>
.signature {
  display: flex;
  flex-direction: column;
  color: var(--neutral-80);
  gap: 0.5rem;

  &.darkmode {
    background-color: var(--neutral-80);
    color: var(--white);

    h6 {
      color: var(--white);
    }

    span.hint {
      svg path:nth-child(2),
      svg path:nth-child(3) {
        stroke: var(--white)
      }
    }
  }

  p {
    line-height: 1.5rem;
  }

  span.hint {
    display: none;

    svg path:nth-child(2),
    svg path:nth-child(3) {
      stroke: var(--neutral-80)
    }
  }

  canvas {
    width: 100%;
    height: auto;
    aspect-ratio: 2.5;
    background-color: var(--white);
    border-radius: 0.5rem;
    position: relative;
    border: 1px solid var(--neutral-80);
  }

  :deep(.base-button) {
    .tertiary {
      background-color: transparent;
      padding: 0;
      width: fit-content;
    }

    svg {
      width: 2rem;
      height: 2rem;
    }

    .tertiary:active,
    .tertiary:focus {
      border: none;
      box-shadow: none;
    }

    .signature :deep(.base-button):last-child {
      flex-direction: initial;
    }
  }

  @media (max-width: 576px) {
    span.hint {
      display: flex;
      flex-direction: row;
      gap: 1rem;
      align-items: center;
    }

    span.hint svg {
      width: 50%;
    }
  }
}
</style>
